"use client";

import { useEffect, useMemo, useState } from "react";
import {
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    TableContainer,
    Input,
    Modal,
    ModalOverlay,
    ModalContent,
    Button,
    useDisclosure,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    VStack,
    Grid,
    GridItem,
    FormControl,
    FormLabel,
    ModalFooter,
    Text,
    HStack,
    Tooltip,
    IconButton,
    Select,
    Textarea,
    Box,
    useToast,
    FormHelperText,
    Spinner,
} from "@chakra-ui/react";
import { DeleteIcon, ViewIcon, EditIcon } from "@chakra-ui/icons";
import { useDispatch, useSelector } from "react-redux";

import {
    fetchSkillDevelopmentAdminasync,
    addSkillDevelopmentAdminAsync,
    deleteSkillDevelopmentAdminAsync,
    updateSkillDevelopmentAdminAsync,
    selectSkillDevelopmentAdmin,
    selectSkillDevelopmentLoading,
} from "../../SkillDevelopmentAdmin/SkillDevelopmentAdminSlice";
import { fetchJobDepartmentsAsync } from "../../JobPostingSlice/JobDepartment/JobDepartmentSlice";
import { RESUME_VIEW, SKILL_DEVELOPMENT_VIEW } from "../../../common/URLs";

// Robustly extract arrays from payloads
const extractArray = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.response)) return payload.response;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.data?.response)) return payload.data.response;
    return [];
};

const isAbsoluteUrl = (url = "") =>
    /^https?:\/\//i.test(url) || url.startsWith("blob:") || url.startsWith("data:");

const SkillDevelopmentAdmin = () => {
    // Modals
    const {
        isOpen: isAddModalOpen,
        onOpen: onOpenAddModal,
        onClose: onCloseAddModal,
    } = useDisclosure();
    const {
        isOpen: isDeleteModalOpen,
        onOpen: onOpenDeleteModal,
        onClose: onCloseDeleteModal,
    } = useDisclosure();
    const {
        isOpen: isViewModalOpen,
        onOpen: onOpenViewModal,
        onClose: onCloseViewModal,
    } = useDisclosure();

    const toast = useToast();
    const dispatch = useDispatch();

    // Redux
    const items = useSelector(selectSkillDevelopmentAdmin) || [];
    const loading = useSelector(selectSkillDevelopmentLoading);

    // Local
    const [jobDepartments, setJobDepartments] = useState([]);
    const [jobDeptLoading, setJobDeptLoading] = useState(false);

    const [deletingItem, setDeletingItem] = useState(null);
    const [viewingItem, setViewingItem] = useState(null);
    const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);

    // Add/Edit state
    const [editingUuid, setEditingUuid] = useState(null);
    const [error, setError] = useState(null);

    // Form
    const [newItem, setNewItem] = useState({
        type: "job_department",
        type_uuid: "",
        title: "",
        description: "",
        attachment: null, // File
        status: "active",
        sort_order: 0,
    });

    // Fetch data
    useEffect(() => {
        dispatch(fetchSkillDevelopmentAdminasync());
        setJobDeptLoading(true);
        dispatch(fetchJobDepartmentsAsync())
            .then((action) => {
                const list = extractArray(action?.payload);
                setJobDepartments(list);
            })
            .finally(() => setJobDeptLoading(false));
    }, [dispatch]);

    // Reset to first page when data changes
    useEffect(() => {
        setCurrentPage(1);
    }, [items?.length]);

    // Pagination
    const itemsPerPage = 15;
    const [currentPage, setCurrentPage] = useState(1);
    const totalPages = Math.max(1, Math.ceil((items?.length || 0) / itemsPerPage));

    useEffect(() => {
        if (currentPage > totalPages) setCurrentPage(totalPages);
    }, [totalPages, currentPage]);

    const paginatedItems = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return (items || []).slice(start, start + itemsPerPage);
    }, [items, currentPage]);

    // Helpers
    const formatLabel = (str) =>
        (str || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

    const statusColor = (status) =>
        (status || "").toLowerCase() === "active"
            ? { bg: "green.100", color: "green.700" }
            : { bg: "red.100", color: "red.700" };

    const getDeptTitle = (uuid) =>
        jobDepartments.find((d) => d.uuid === uuid)?.title || "-";

    // Handlers
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewItem((prev) => ({
            ...prev,
            [name]: name === "sort_order" ? Number(value) : value,
        }));
    };

    const handlePdfChange = (e) => {
        const file = e.target.files?.[0] || null;
        if (file && file.type !== "application/pdf") {
            toast({
                title: "Invalid file",
                description: "Please select a PDF file.",
                status: "error",
                duration: 3000,
                isClosable: true,
                position: "top",
            });
            return;
        }
        setNewItem((prev) => ({ ...prev, attachment: file }));
    };

    const startAdd = () => {
        setEditingUuid(null);
        setError(null);
        setNewItem({
            type: "job_department",
            type_uuid: "",
            title: "",
            description: "",
            attachment: null,
            status: "active",
            sort_order: 0,
        });
        onOpenAddModal();
    };

    // Prefill type_uuid robustly for edit
    const startEdit = (item) => {
        setEditingUuid(item.uuid);
        setError(null);

        const derivedTypeUuid =
            item.type_uuid ||
            item.job_department_uuid ||
            item.job_department?.uuid ||
            "";

        setNewItem({
            type: "job_department",
            type_uuid: derivedTypeUuid,
            title: item.title || "",
            description: item.description || "",
            attachment: null, // choose a new file to replace
            status: item.status || "active",
            sort_order: Number(item.sort_order || 0),
        });
        onOpenAddModal();
    };

    // Build FormData for both add and update (consistent with candidate profile pattern)
    const buildFormData = (data) => {
        const fd = new FormData();
        fd.append("type", "job_department");
        fd.append("type_uuid", data.type_uuid ?? "");
        fd.append("title", (data.title || "").trim());
        fd.append("description", (data.description || "").trim());
        fd.append("sort_order", String(data.sort_order ?? "0"));
        fd.append("status", data.status || "active");
        if (data.attachment instanceof File) {
            fd.append("attachment", data.attachment); // binary
        }
        return fd;
    };

    const handleSave = async () => {
        setError(null);

        if (!newItem.title?.trim() || !newItem.description?.trim()) {
            setError("Title and Description are required.");
            return;
        }
        if (!newItem.type_uuid) {
            setError("Please select a Job Department.");
            return;
        }

        try {
            const formData = buildFormData(newItem);

            if (editingUuid) {
                // Laravel-friendly: send POST with _method=PUT for multipart updates
                await dispatch(
                    updateSkillDevelopmentAdminAsync({ uuid: editingUuid, data: formData })
                ).unwrap();

                toast({
                    title: "Skill item updated",
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                    position: "top-right",
                });
            } else {
                await dispatch(addSkillDevelopmentAdminAsync(formData)).unwrap();
                toast({
                    title: "Skill item added",
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                    position: "top-right",
                });
                setCurrentPage(1); // show newest at the top
            }

            // Refresh so UI matches backend (and to get any processed fields back)
            await dispatch(fetchSkillDevelopmentAdminasync());

            onCloseAddModal();
            setEditingUuid(null);
            setNewItem({
                type: "job_department",
                type_uuid: "",
                title: "",
                description: "",
                attachment: null,
                status: "active",
                sort_order: 0,
            });
        } catch (err) {
            const msg =
                typeof err === "string"
                    ? err
                    : err?.message ||
                    (editingUuid ? "Failed to update item" : "Failed to add item");
            setError(msg);
            toast({
                title: "Error",
                description: msg,
                status: "error",
                duration: 3500,
                isClosable: true,
                position: "top-right",
            });
        }
    };

    const openDeleteModal = (item) => {
        setDeletingItem(item);
        onOpenDeleteModal();
    };

    const handleDelete = async () => {
        if (!deletingItem?.uuid) return;
        try {
            await dispatch(
                deleteSkillDevelopmentAdminAsync(deletingItem.uuid)
            ).unwrap();
            toast({
                title: "Item deleted",
                status: "success",
                duration: 3000,
                isClosable: true,
                position: "top-right",
            });
            // Refresh after delete
            await dispatch(fetchSkillDevelopmentAdminasync());
        } catch (e) {
            toast({
                title: "Failed to delete",
                description: e?.message || "",
                status: "error",
                duration: 3000,
                isClosable: true,
                position: "top-right",
            });
        } finally {
            setDeletingItem(null);
            onCloseDeleteModal();
        }
    };

    const handleView = (item) => {
        setViewingItem(item);
        onOpenViewModal();
    };

    // Build a final URL for viewing the attachment (File, absolute, or relative path)
    const buildAttachmentUrl = (attachment) => {
        if (!attachment) return null;

        if (attachment instanceof File) {
            // Local blob preview
            return URL.createObjectURL(attachment);
        }

        if (typeof attachment === "string" && attachment.trim() !== "") {
            return isAbsoluteUrl(attachment)
                ? attachment
                : `${SKILL_DEVELOPMENT_VIEW}${attachment}`;
        }

        return null;
    };

    // Safer preview effect w/ cleanup
    useEffect(() => {
        let objUrl = null;

        if (viewingItem?.attachment instanceof File) {
            objUrl = buildAttachmentUrl(viewingItem.attachment);
            setPdfPreviewUrl(objUrl);
        } else if (typeof viewingItem?.attachment === "string") {
            setPdfPreviewUrl(buildAttachmentUrl(viewingItem.attachment));
        } else {
            setPdfPreviewUrl(null);
        }

        return () => {
            if (objUrl) URL.revokeObjectURL(objUrl);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewingItem]);

    const openPdf = (url) => {
        if (!url) {
            console.error("PDF URL is missing.");
            return;
        }
        const newTab = window.open(url, "_blank", "noopener,noreferrer");
        if (!newTab) {
            console.error("Failed to open PDF. Please check your browser settings.");
        }
    };

    // Pagination handlers
    const handleNextPage = () => {
        if (currentPage < totalPages) setCurrentPage((p) => p + 1);
    };
    const handlePrevPage = () => {
        if (currentPage > 1) setCurrentPage((p) => p - 1);
    };

    return (
        <div
            className="h-screen bg-adminbg contentAdmin p-4"
            style={{ fontFamily: "Inter, sans-serif" }}
        >
            <div className="">
                <div className="w-full my-5 bg-white rounded-xl">
                    <div className="flex justify-between">
                        <p className="text-xl text-black font-semibold px-3 py-7">
                            Skill Development Management
                        </p>

                        <div className="py-4 px-3 flex gap-3">
                            <Button colorScheme="blue" fontSize="medium" onClick={startAdd}>
                                Add<i className="fa-solid fa-plus px-2"></i>
                            </Button>

                            {/* Add/Edit Modal */}
                            <Modal isOpen={isAddModalOpen} onClose={onCloseAddModal} size="4xl">
                                <ModalOverlay />
                                <ModalContent>
                                    <ModalHeader bg="blue.500" color="white" borderTopRadius="md">
                                        {editingUuid ? "Edit Skill Development" : "Add Skill Development"}
                                    </ModalHeader>
                                    <ModalBody py={6}>
                                        <VStack spacing={6}>
                                            <Grid templateColumns="repeat(2, 1fr)" gap={4} width="100%">
                                                <GridItem colSpan={[2, 1]}>
                                                    <FormControl isRequired>
                                                        <FormLabel>Type</FormLabel>
                                                        <Select name="type" value="job_department" isDisabled>
                                                            <option value="job_department">job_department</option>
                                                        </Select>
                                                    </FormControl>
                                                </GridItem>

                                                <GridItem colSpan={[2, 1]}>
                                                    <FormControl isRequired>
                                                        <FormLabel>Job Department</FormLabel>
                                                        <Select
                                                            name="type_uuid"
                                                            value={newItem.type_uuid}
                                                            onChange={handleInputChange}
                                                            placeholder={
                                                                jobDeptLoading ? "Loading..." : "Select job department"
                                                            }
                                                            isDisabled={jobDeptLoading}
                                                        >
                                                            {jobDepartments
                                                                ?.filter((d) => d.status === "active")
                                                                ?.map((dept) => (
                                                                    <option key={dept.uuid} value={dept.uuid}>
                                                                        {dept.title}
                                                                    </option>
                                                                ))}
                                                        </Select>
                                                        <FormHelperText>
                                                            Sends department uuid as type_uuid
                                                        </FormHelperText>
                                                    </FormControl>
                                                </GridItem>

                                                <GridItem colSpan={[2, 1]}>
                                                    <FormControl isRequired>
                                                        <FormLabel>Title</FormLabel>
                                                        <Input
                                                            name="title"
                                                            placeholder="Enter title"
                                                            value={newItem.title}
                                                            onChange={handleInputChange}
                                                        />
                                                    </FormControl>
                                                </GridItem>

                                                <GridItem colSpan={2}>
                                                    <FormControl isRequired>
                                                        <FormLabel>Description</FormLabel>
                                                        <Textarea
                                                            name="description"
                                                            placeholder="Enter description"
                                                            value={newItem.description}
                                                            onChange={handleInputChange}
                                                            rows={4}
                                                        />
                                                    </FormControl>
                                                </GridItem>

                                                <GridItem colSpan={[2, 1]}>
                                                    <FormControl>
                                                        <FormLabel>PDF (Attachment)</FormLabel>
                                                        <Input
                                                            type="file"
                                                            accept="application/pdf"
                                                            onChange={handlePdfChange}
                                                        />
                                                        <FormHelperText>
                                                            The selected PDF will be uploaded as binary with field name
                                                            "attachment".
                                                        </FormHelperText>
                                                        {newItem.attachment && (
                                                            <Text mt={2} fontSize="sm" color="gray.600">
                                                                Selected: {newItem.attachment.name}
                                                            </Text>
                                                        )}
                                                    </FormControl>
                                                </GridItem>

                                                <GridItem colSpan={[2, 1]}>
                                                    <FormControl isRequired>
                                                        <FormLabel>Status</FormLabel>
                                                        <Select
                                                            name="status"
                                                            value={newItem.status}
                                                            onChange={handleInputChange}
                                                        >
                                                            <option value="active">active</option>
                                                            <option value="inactive">inactive</option>
                                                        </Select>
                                                    </FormControl>
                                                </GridItem>

                                                <GridItem colSpan={[2, 1]}>
                                                    <FormControl>
                                                        <FormLabel>Sort Order</FormLabel>
                                                        <Input
                                                            name="sort_order"
                                                            type="number"
                                                            placeholder="0"
                                                            step={1}
                                                            value={newItem.sort_order}
                                                            onChange={handleInputChange}
                                                        />
                                                    </FormControl>
                                                </GridItem>
                                            </Grid>

                                            {error && <Text color="red.500">{error}</Text>}
                                        </VStack>
                                    </ModalBody>
                                    <ModalFooter bg="gray.50" borderBottomRadius="md">
                                        <Button colorScheme="blue" mr={3} onClick={handleSave} isDisabled={loading}>
                                            {loading ? <Spinner size="sm" mr={2} /> : null}
                                            {editingUuid ? "Update" : "Save"}
                                        </Button>
                                        <Button
                                            onClick={() => {
                                                setEditingUuid(null);
                                                onCloseAddModal();
                                            }}
                                        >
                                            Cancel
                                        </Button>
                                    </ModalFooter>
                                </ModalContent>
                            </Modal>

                            {/* Delete Confirmation Modal */}
                            <Modal isOpen={isDeleteModalOpen} onClose={onCloseDeleteModal}>
                                <ModalOverlay />
                                <ModalContent maxW="2xl" borderRadius="lg" boxShadow="lg">
                                    <ModalHeader fontSize="lg" fontWeight="bold" textAlign="center">
                                        Confirm Deletion
                                    </ModalHeader>
                                    <ModalCloseButton />
                                    <ModalBody>
                                        <Text fontSize="md" textAlign="center">
                                            Are you sure you want to delete{" "}
                                            <Text as="span" fontWeight="semibold" color="red.500">
                                                "{deletingItem?.title}"
                                            </Text>
                                            ?
                                        </Text>
                                    </ModalBody>
                                    <ModalFooter display="flex" gap="2" justifyContent="center">
                                        <Button colorScheme="red" w="40" onClick={handleDelete} isDisabled={loading}>
                                            Delete
                                        </Button>
                                        <Button
                                            variant="outline"
                                            colorScheme="red"
                                            w="40"
                                            onClick={onCloseDeleteModal}
                                        >
                                            Cancel
                                        </Button>
                                    </ModalFooter>
                                </ModalContent>
                            </Modal>

                            {/* View Item Modal */}
                            <Modal isOpen={isViewModalOpen} onClose={onCloseViewModal} size="5xl">
                                <ModalOverlay />
                                <ModalContent>
                                    <ModalHeader bg="blue.500" color="white" borderTopRadius="md">
                                        View Skill Item
                                    </ModalHeader>
                                    <ModalCloseButton color="white" />
                                    <ModalBody py={6}>
                                        {viewingItem && (
                                            <VStack spacing={4} align="start" width="100%">
                                                <Box width="100%">
                                                    <Text fontWeight="bold">Type:</Text>
                                                    <Text>{formatLabel(viewingItem.type)}</Text>
                                                </Box>
                                                <Box width="100%">
                                                    <Text fontWeight="bold">Department:</Text>
                                                    <Text>{viewingItem?.job_department?.title}</Text>
                                                </Box>
                                                <Box width="100%">
                                                    <Text fontWeight="bold">Title:</Text>
                                                    <Text>{viewingItem.title}</Text>
                                                </Box>
                                                <Box width="100%">
                                                    <Text fontWeight="bold">Description:</Text>
                                                    <Text whiteSpace="pre-wrap">{viewingItem.description}</Text>
                                                </Box>
                                                <Box width="100%">
                                                    <Text fontWeight="bold">Status:</Text>
                                                    <Text>{formatLabel(viewingItem.status)}</Text>
                                                </Box>
                                                <Box width="100%">
                                                    <Text fontWeight="bold">Sort Order:</Text>
                                                    <Text>{viewingItem.sort_order}</Text>
                                                </Box>
                                                {(() => {
                                                    const url = pdfPreviewUrl || buildAttachmentUrl(viewingItem?.attachment);
                                                    return url ? (
                                                        <Box width="100%">
                                                            <Text fontWeight="bold">Attachment:</Text>
                                                            <HStack mt={2} spacing={3}>
                                                                <Button
                                                                    colorScheme="blue"
                                                                    size="sm"
                                                                    onClick={() => openPdf(url)}
                                                                >
                                                                    Open PDF
                                                                </Button>
                                                                {/* Optional inline preview (small) */}
                                                                <Box w="100%" maxH="400px" border="1px solid #e2e8f0">
                                                                    <iframe
                                                                        src={url}
                                                                        title="PDF Preview"
                                                                        width="100%"
                                                                        height="400px"
                                                                    />
                                                                </Box>
                                                            </HStack>
                                                        </Box>
                                                    ) : null;
                                                })()}
                                            </VStack>
                                        )}
                                    </ModalBody>
                                    <ModalFooter bg="gray.50" borderBottomRadius="md">
                                        <Button onClick={onCloseViewModal}>Close</Button>
                                    </ModalFooter>
                                </ModalContent>
                            </Modal>
                        </div>
                    </div>

                    {/* Table */}
                    <TableContainer>
                        <Table size="sm">
                            <Thead className="bg-gray-200">
                                <Tr>
                                    <Th className="w-1/12">S.No</Th>
                                    <Th className="w-1/6">Type</Th>
                                    <Th className="w-1/4">Department</Th>
                                    <Th className="w-1/4">Title</Th>
                                    <Th className="w-1/4">Description</Th>
                                    <Th className="w-1/12">Status</Th>
                                    <Th className="w-1/12">Sort</Th>
                                    <Th className="w-1/6">Actions</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {loading ? (
                                    <Tr>
                                        <Td colSpan={8} className="text-center py-4 text-gray-500">
                                            <HStack justify="center" align="center">
                                                <Spinner size="sm" />
                                                <Text>Loading...</Text>
                                            </HStack>
                                        </Td>
                                    </Tr>
                                ) : (paginatedItems || []).length > 0 ? (
                                    paginatedItems.map((item, idx) => {
                                        const rowIndex = (currentPage - 1) * itemsPerPage + idx + 1;
                                        return (
                                            <Tr key={item.uuid || item.id || rowIndex}>
                                                <Td>{rowIndex}</Td>
                                                <Td>{formatLabel(item.type)}</Td>
                                                <Td>{item?.job_department?.title || getDeptTitle(item.type_uuid)}</Td>
                                                <Td className="truncate">


                                                    {item.title}


                                                </Td>
                                                <Td title={item.description || ""}>
                                                    {item.description?.length > 40
                                                        ? `${item.description.substring(0, 40)}...`
                                                        : item.description}
                                                </Td>
                                                <Td>
                                                    <Text
                                                        px={2}
                                                        py={1}
                                                        borderRadius="md"
                                                        bg={statusColor(item.status).bg}
                                                        color={statusColor(item.status).color}
                                                        display="inline-block"
                                                        fontSize="sm"
                                                    >
                                                        {formatLabel(item.status)}
                                                    </Text>
                                                </Td>
                                                <Td>{item.sort_order}</Td>
                                                <Td>
                                                    <HStack spacing={2}>
                                                        <Tooltip label="View Item" placement="top">
                                                            <IconButton
                                                                icon={<ViewIcon />}
                                                                colorScheme="teal"
                                                                size="sm"
                                                                onClick={() => handleView(item)}
                                                                aria-label="View item"
                                                            />
                                                        </Tooltip>
                                                        <Tooltip label="Edit Item" placement="top">
                                                            <IconButton
                                                                icon={<EditIcon />}
                                                                colorScheme="blue"
                                                                size="sm"
                                                                onClick={() => startEdit(item)}
                                                                aria-label="Edit item"
                                                            />
                                                        </Tooltip>
                                                        <Tooltip label="Delete Item" placement="top">
                                                            <IconButton
                                                                icon={<DeleteIcon />}
                                                                colorScheme="red"
                                                                size="sm"
                                                                onClick={() => openDeleteModal(item)}
                                                                aria-label="Delete item"
                                                            />
                                                        </Tooltip>
                                                    </HStack>
                                                </Td>
                                            </Tr>
                                        );
                                    })
                                ) : (
                                    <Tr>
                                        <Td colSpan={8} className="text-center py-4 text-gray-500">
                                            No records available
                                        </Td>
                                    </Tr>
                                )}
                            </Tbody>
                        </Table>
                    </TableContainer>
                </div>
            </div>

            {/* Pagination */}
            <GridItem colSpan={2} display="flex" justifyContent="center">
                <div className="flex w-full justify-evenly items-center mt-4">
                    <button
                        className={`bg-gray-300 text-black px-4 py-2 rounded ${currentPage === 1 ? "opacity-50 cursor-not-allowed" : ""
                            }`}
                        onClick={handlePrevPage}
                        disabled={currentPage === 1}
                    >
                        Previous
                    </button>
                    <span>
                        Page {currentPage} of {totalPages}
                    </span>
                    <button
                        className={`bg-blue-500 text-white px-4 py-2 rounded ${currentPage === totalPages ? "opacity-50 cursor-not-allowed" : ""
                            }`}
                        onClick={handleNextPage}
                        disabled={currentPage === totalPages}
                    >
                        Next
                    </button>
                </div>
            </GridItem>
        </div>
    );
};

export default SkillDevelopmentAdmin;